import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { Tabs, Tab, makeStyles } from "@material-ui/core";
import { TabPanel, TabContext } from "@material-ui/lab";
import theme from "styles/theme";
import { formatMessage } from "lib/globalize-util";
import InsightsHeader from "./insights-header";
import InsightsSidePanelContainer from "./insights-side-panel-container";
import { InsightsActions } from "constants/analytics";
import type { IStore } from "reducers";
import { INSIGHTS } from "constants/integration/insights";

const useStyles = makeStyles({
    tabRoot: {
        padding: "24px 0px",
    },
    tabs: {
        "& button": {
            minWidth: "25%",
        },
        "& .MuiTab-root": {
            textTransform: "none",
            fontSize: "1.3rem",
            color: theme.palette.textColor,
        },
    },
    tabsIndicator: {
        backgroundColor: "#2dbeb4",
    },
    learnMore: {
        fontSize: "1.3rem",
        textDecorationLine: "underline",
        textUnderlineOffset: "5px",
        color: theme.palette.primary1Color,
        display: "block",
        marginTop: "20px",
    },
});

interface IOrderInsightsProductStylesProps {
    onToggleInsightsSidePanel: () => void;
    tabs: {
        label: string;
        key: string;
        component: JSX.Element;
    }[];
    insights?: IStore["insights"];
    productsLoading?: any;
    isLoadingInsights: boolean;
}

const getDefaultTab = (tabs, insights) => {
    const hasProductSizes = Object.keys(insights.productSizes)?.length > 0;

    if (hasProductSizes) {
        const sizesTab = tabs.find((tab) => tab.key === INSIGHTS.COMPONENTS.SIZE);
        if (sizesTab) {
            return sizesTab;
        }
    }

    const hasTopProducts = insights.productStyles?.immediate?.length > 0 || insights.productStyles?.prebook?.length > 0;

    if (hasTopProducts) {
        const productTab = tabs.find((tab) => tab.key === INSIGHTS.COMPONENTS.STYLE);
        if (productTab) {
            return productTab;
        }
    }

    const hasCategory = insights.categories?.length > 0;

    if (hasCategory) {
        const categoryTab = tabs.find((tab) => tab.key === INSIGHTS.COMPONENTS.CATEGORY);
        if (categoryTab) {
            return categoryTab;
        }
    }

    const hasColor = insights.productColors?.length > 0;

    if (hasColor) {
        const colorTab = tabs.find((tab) => tab.key === INSIGHTS.COMPONENTS.COLOR);
        if (colorTab) {
            return colorTab;
        }
    }

    return tabs[0];
};

const OrderInsightsPanelContent: React.FC<IOrderInsightsProductStylesProps> = ({
    onToggleInsightsSidePanel,
    tabs,
    insights,
    isLoadingInsights,
}) => {
    const classes = useStyles();

    const [currentTab, setCurrentTab] = React.useState(tabs[0].label);
    const [sizeTabPresent, setSizeTabPresent] = React.useState(false);
    const [defaultTabHasBeenSet, setDefaultTabHasBeenSet] = React.useState(false);

    const calculateActiveTab = useCallback(
        () => {
            if (!isLoadingInsights) {
                if (!defaultTabHasBeenSet) {
                    setCurrentTab(getDefaultTab(tabs, insights).label);
                    setDefaultTabHasBeenSet(true);
                    setSizeTabPresent(!!tabs.find((tab) => tab.key === INSIGHTS.COMPONENTS.SIZE));
                }

                // if size tab gets added after default has been set
                if (defaultTabHasBeenSet && !sizeTabPresent) {
                    setSizeTabPresent(!!tabs.find((tab) => tab.key === INSIGHTS.COMPONENTS.SIZE));
                }

                // check if the sizes tab is now not present and set new default tab
                if (sizeTabPresent && !tabs.find((tab) => tab.key === INSIGHTS.COMPONENTS.SIZE)) {
                    setSizeTabPresent(false);
                    setCurrentTab(getDefaultTab(tabs, insights).label);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            tabs,
            insights,
            sizeTabPresent,
            defaultTabHasBeenSet,
            setCurrentTab,
            setDefaultTabHasBeenSet,
            setSizeTabPresent,
        ],
    );

    const getActiveTab = useMemo(() => {
        if (sizeTabPresent && !tabs.some((tab) => tab.key === INSIGHTS.COMPONENTS.SIZE)) {
            calculateActiveTab();
        }

        return currentTab;
    }, [sizeTabPresent, currentTab, tabs, calculateActiveTab]);

    useEffect(() => {
        calculateActiveTab();
    }, [isLoadingInsights, calculateActiveTab]);

    const handleCurrentTabChange = (_, newValue: string) => {
        global.analytics.track(InsightsActions.tabClick, {
            Tab: newValue,
        });
        if (newValue === "Products") {
            global.analytics.track(InsightsActions.productsTabClick);
        } else if (newValue === "Categories") {
            global.analytics.track(InsightsActions.categoriesTabClick);
        } else if (newValue === "Colors") {
            global.analytics.track(InsightsActions.colorsTabClick);
        } else if (newValue === "Sizes") {
            global.analytics.track(InsightsActions.sizesTabClick);
        }
        setCurrentTab(newValue);
    };

    const handleHelpdeskClick = async (e) => {
        e.preventDefault();
        await global.analytics.track(InsightsActions.helpdeskClick);
        window.open(
            "https://helpdesk.nuorder.com/hc/en-us/articles/27071765927195-Order-Insights-overview-beta",
            "_blank",
            "noopener,noreferrer",
        );
    };

    // Fix the issue when opening closing the PDP drawer
    // This hack allows to update the tab indicator on container resize
    const containerRef = useRef(null);
    const actionRef = useRef(null);
    useEffect(() => {
        const ro = new ResizeObserver(() => {
            if (actionRef.current && containerRef.current) {
                actionRef.current.updateIndicator();
            }
        });

        if (containerRef.current) {
            ro.observe(containerRef.current);
        }

        return () => {
            ro.disconnect();
        };
    }, []);

    return (
        <InsightsSidePanelContainer ref={containerRef}>
            <TabContext value={getActiveTab}>
                <InsightsHeader onToggleInsightsSidePanel={onToggleInsightsSidePanel}>
                    <Tabs
                        action={actionRef}
                        value={getActiveTab}
                        onChange={handleCurrentTabChange}
                        classes={{ root: classes.tabs, indicator: classes.tabsIndicator }}
                        centered
                        variant="fullWidth"
                        className={classes.tabs}
                    >
                        {tabs.map((tab, index) => (
                            <Tab
                                key={index}
                                label={tab.label}
                                value={tab.label}
                                data-test={`${INSIGHTS.TAB}-${tab.label.toLowerCase()}`}
                            />
                        ))}
                    </Tabs>
                </InsightsHeader>
                {tabs.map((tab, index) => (
                    <TabPanel key={index} value={tab.label} classes={{ root: classes.tabRoot }}>
                        {tab.component}
                        <a
                            className={classes.learnMore}
                            onClick={handleHelpdeskClick}
                            data-test={INSIGHTS.HELP_DESK_LINK}
                        >
                            {formatMessage("Learn more about Order Trends")}
                        </a>
                    </TabPanel>
                ))}
            </TabContext>
        </InsightsSidePanelContainer>
    );
};

export default OrderInsightsPanelContent;
